import { Globe, Briefcase, Smartphone, Search, Wrench, ArrowRight, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { motion, useInView, useScroll, useTransform, useSpring, useReducedMotion } from 'motion/react';
import { useMemo, useRef } from 'react';

export function Services() {
    const ref = useRef(null);
    const reduceMotion = useReducedMotion();
    const isInView = useInView(ref, { once: true, margin: '-120px' });

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end start'],
    });

    const gridYRaw = useTransform(scrollYProgress, [0, 1], ['10%', '-10%']);
    const gridY = useSpring(gridYRaw, { stiffness: 80, damping: 22 });

    // E-Commerce raus
    const services = useMemo(
        () => [
            {
                icon: Globe,
                title: 'Unternehmenswebsites',
                description:
                    'Professionelle Websites, die Ihre Marke klar positionieren, Vertrauen schaffen und Anfragen generieren.',
                tag: 'Brand & Vertrauen',
            },
            {
                icon: Briefcase,
                title: 'Landing Pages',
                description:
                    'Conversion-optimierte Seiten für Kampagnen, Launches und Lead-Generierung – schnell, fokussiert, messbar.',
                tag: 'Conversion',
            },
            {
                icon: Smartphone,
                title: 'Responsive Design',
                description:
                    'Mobile-first Entwicklung für perfekte Darstellung auf Smartphone, Tablet und Desktop – ohne Kompromisse.',
                tag: 'Mobile-first',
            },
            {
                icon: Search,
                title: 'SEO & Performance',
                description:
                    'Technisches SEO, saubere Struktur und Core Web Vitals – für bessere Sichtbarkeit und schnelle Ladezeiten.',
                tag: 'Core Web Vitals',
            },
            {
                icon: Wrench,
                title: 'Wartung & Support',
                description:
                    'Updates, Sicherheit, Backups und Support – damit Ihre Website langfristig stabil, sicher und aktuell bleibt.',
                tag: 'Sicherheit',
            },
        ],
        []
    );

    return (
        <section id="dienstleistungen" className="relative py-24 px-4 overflow-hidden" ref={ref}>
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50" />

            {/* Animated grid overlay */}
            <motion.div
                className="absolute inset-0 opacity-[0.14]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.14) 1px, transparent 1px)',
                    backgroundSize: '56px 56px',
                    y: gridY,
                    maskImage: 'radial-gradient(circle at 50% 30%, black, transparent 70%)',
                }}
                aria-hidden="true"
            />

            {/* Ambient blobs */}
            <motion.div
                className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-30"
                style={{
                    background: 'radial-gradient(circle, rgba(168,85,247,0.35), transparent 60%)',
                }}
                animate={
                    reduceMotion ? undefined : { scale: [1, 1.08, 1], opacity: [0.22, 0.34, 0.22] }
                }
                transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />
            <motion.div
                className="absolute -bottom-32 -left-24 w-[560px] h-[560px] rounded-full blur-3xl opacity-25"
                style={{
                    background: 'radial-gradient(circle, rgba(59,130,246,0.35), transparent 60%)',
                }}
                animate={
                    reduceMotion ? undefined : { scale: [1, 1.1, 1], opacity: [0.18, 0.3, 0.18] }
                }
                transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    className="text-center mb-14"
                    initial={{ opacity: 0, y: 26 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-border/60 bg-white/70 backdrop-blur px-4 py-2 shadow-sm">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-white">
              <Sparkles className="h-4 w-4" />
            </span>
                        <span className="text-sm text-gray-700">Meine Dienstleistungen</span>
                        <span className="ml-1 rounded-full bg-black/5 px-2 py-0.5 text-xs text-gray-700">
              Klar. Schnell. Messbar.
            </span>
                    </div>

                    <h2 className="mt-6 text-5xl md:text-6xl tracking-tight">
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Lösungen
            </span>{' '}
                        <span className="text-gray-900">für Ihren digitalen Erfolg</span>
                    </h2>

                    <p className="mt-5 text-xl text-gray-600 max-w-2xl mx-auto">
                        Von der Unternehmenswebsite bis zur Landing Page: modern, performant und auf Ergebnisse optimiert.
                    </p>
                </motion.div>

                {/* Cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
                    {services.map((service, index) => {
                        const Icon = service.icon;
                        return (
                            <motion.div
                                key={service.title}
                                initial={{ opacity: 0, y: 34 }}
                                animate={isInView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.55, delay: 0.08 * index }}
                                whileHover={{ y: -8 }}
                            >
                                <Card className="group relative h-full overflow-hidden border border-gray-200/70 bg-white/75 backdrop-blur shadow-lg hover:shadow-xl transition-shadow rounded-2xl">
                                    {/* gradient glow */}
                                    <div className="pointer-events-none absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-blue-500/20" />

                                    <CardHeader className="relative">
                                        <div className="flex items-start justify-between gap-4">
                                            <motion.div
                                                className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center shadow-md"
                                                whileHover={{ rotate: 8, scale: 1.05 }}
                                                transition={{ type: 'spring', stiffness: 260, damping: 18 }}
                                            >
                                                <Icon className="w-6 h-6 text-white" />
                                            </motion.div>

                                            <span className="inline-flex items-center gap-2 rounded-full bg-black/5 px-3 py-1 text-xs text-gray-700 border border-gray-200/70">
                        {service.tag}
                      </span>
                                        </div>

                                        <CardTitle className="mt-4 text-xl">{service.title}</CardTitle>
                                    </CardHeader>

                                    <CardContent className="relative">
                                        <CardDescription className="text-base leading-relaxed">
                                            {service.description}
                                        </CardDescription>

                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}

                    {/* Premium CTA card */}
                    <motion.div
                        className="md:col-span-2 lg:col-span-1"
                        initial={{ opacity: 0, y: 34 }}
                        animate={isInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.55, delay: 0.08 * services.length }}
                    >
                        <div className="relative h-full overflow-hidden rounded-2xl border border-gray-200/70 bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-xl">
                            <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_55%)]" />
                            <div className="relative p-7 h-full flex flex-col">
                                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs w-fit">
                                    <Sparkles className="h-4 w-4" />
                                    Kostenloses Erstgespräch
                                </div>

                                <div className="mt-4 text-2xl font-semibold leading-tight">
                                    Lassen Sie uns Ihr Projekt gemeinsam stark machen.
                                </div>

                                <div className="mt-3 text-white/85 leading-relaxed">
                                    In 20 Minuten klären wir Ziele, Umfang & den besten Weg. Danach wissen Sie genau, wie es weitergeht.
                                </div>

                                <button
                                    type="button"
                                    onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                                    className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl bg-white text-purple-700 px-5 py-3 font-medium shadow-md hover:bg-white/95 transition-colors"
                                >
                                    Termin anfragen
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
